import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../cn';

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-neutral-950 dark:[&>svg]:text-neutral-100',
  {
    variants: {
      variant: {
        default: 'bg-white text-neutral-950 border-neutral-200 dark:bg-neutral-900 dark:text-neutral-100 dark:border-neutral-700',
        destructive:
          'border-red-500/50 text-red-600 [&>svg]:text-red-600 bg-red-50 dark:bg-red-950/40 dark:text-red-300 dark:[&>svg]:text-red-300',
        success:
          'border-green-500/50 text-green-700 [&>svg]:text-green-600 bg-green-50 dark:bg-green-950/40 dark:text-green-300 dark:[&>svg]:text-green-300',
        warning:
          'border-yellow-500/50 text-yellow-700 [&>svg]:text-yellow-600 bg-yellow-50 dark:bg-yellow-950/40 dark:text-yellow-300 dark:[&>svg]:text-yellow-300',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 font-medium leading-none tracking-tight', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription, alertVariants };
